import { Anchor, Button } from "@newsela/angelou";
import { type RefObject } from "react";

import { type GenerationGeniusAPIResponse } from "@/types/models/GenerationGenius.interface";
import { VideoSubject } from "@/types/models/UnlevelledContent.interface";
import PanelHeader from "@/view/PanelHeader";
import {
  type IGeniusButtonEventProperties,
  RESOURCE_NAMES,
} from "@/view/UnlevelledContent/UnlevelledContentActivityPanel/UnlevelledContentActivityPanel.constant";
import { formatGeniusActivityPanelContent } from "@/view/UnlevelledContent/utils";
import {
  drawerContainerCss,
  instructionsCss,
  resourceBtnCss,
  subheadingCss,
  titleCss,
} from "./UnlevelledContentTeacherResourcesContainer.style";

interface GeniusTeacherResourcesProps {
  backButtonEventProperties: IGeniusButtonEventProperties;
  closeButtonEventProperties: IGeniusButtonEventProperties;
  onClickBackButton: () => void;
  onClickCloseButton?: () => void;
  shouldFocusCloseButtonRef: RefObject<boolean>;
  contentData: GenerationGeniusAPIResponse;
  subject: string;
}

export const UnlevelledContentTeacherResourcesContainer = ({
  backButtonEventProperties,
  closeButtonEventProperties,
  onClickBackButton,
  onClickCloseButton,
  shouldFocusCloseButtonRef,
  contentData,
  subject,
}: GeniusTeacherResourcesProps) => {
  const { pdfResources = [] } = formatGeniusActivityPanelContent(contentData);

  const teacherGuide = pdfResources.find(
    (resource) => resource.type === "teacher_guide",
  );

  const lessonPlan = pdfResources.find(
    (resource) => resource.type === "lesson_plan",
  );

  // Only display student activity pdfs for Language Arts videos
  const studentActivity = pdfResources.find(
    (resource) =>
      subject === VideoSubject.LANGUAGE_ARTS &&
      resource.type === "student_activity",
  );

  const instructions =
    "Download the full lesson plan to save time and feel confident leading an engaging, standards-aligned lesson. You can also download the Teacher Information PDF for helpful context, including background information, tips, and misconceptions.";
  const elaInstructions =
    "Download the full lesson plan to save time and feel confident leading an engaging, standards-aligned lesson. This section includes ideas for activities you can use alongside this video in your classroom.";

  return (
    <>
      <PanelHeader
        onClickBackButton={onClickBackButton}
        onClickCloseButton={onClickCloseButton}
        backButtonEventProperties={backButtonEventProperties}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <h2 css={titleCss}>Teacher Resources</h2>
      </PanelHeader>

      <div css={drawerContainerCss}>
        <h3 css={subheadingCss}>Download Teacher Resources</h3>
        <p css={instructionsCss}>
          {subject === VideoSubject.LANGUAGE_ARTS
            ? elaInstructions
            : instructions}
        </p>
        {[lessonPlan, teacherGuide, studentActivity].map((resource) => {
          const resourceName = resource
            ? (RESOURCE_NAMES[resource.type] ?? "Resource")
            : "Resource";

          return resource ? (
            <Anchor
              openInNewTab
              href={resource?.url}
              styleAnchorAsButton={{
                flavor: Button.flavors.muted,
                isFullWidth: true,
              }}
              __cssFor={resourceBtnCss}
            >
              {resourceName} (PDF)
            </Anchor>
          ) : null;
        })}
      </div>
    </>
  );
};

export default UnlevelledContentTeacherResourcesContainer;
